// Security utility functions for GoalFlex Frontend

/**
 * Sanitize user input to prevent XSS attacks
 * @param {string} input - User input to sanitize
 * @returns {string} Sanitized string
 */
export const sanitizeInput = (input) => {
  if (!input || typeof input !== "string") return input;

  // Remove potentially dangerous characters and patterns
  return input
    .replace(/[<>]/g, "") // Remove < and >
    .replace(/javascript:/gi, "") // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, "") // Remove event handlers like onclick=
    .trim();
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {object} { isValid, message }
 */
export const validatePassword = (password) => {
  if (!password) {
    return { isValid: false, message: "Password is required" };
  }
  if (password.length < 8) {
    return {
      isValid: false,
      message: "Password must be at least 8 characters",
    };
  }
  // Add more rules as needed
  return { isValid: true, message: "" };
};

/**
 * Escape HTML to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
export const escapeHtml = (text) => {
  if (!text || typeof text !== "string") return text;

  const map = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    "/": "&#x2F;",
  };

  return text.replace(/[&<>"'/]/g, (char) => map[char]);
};

/**
 * Rate limiter for login attempts
 */
class RateLimiter {
  constructor(maxAttempts = 5, windowMs = 15 * 60 * 1000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
    this.attempts = new Map();
  }

  check(identifier) {
    const now = Date.now();
    const userAttempts = this.attempts.get(identifier) || {
      count: 0,
      firstAttempt: now,
    };

    // Reset if window has passed
    if (now - userAttempts.firstAttempt > this.windowMs) {
      userAttempts.count = 0;
      userAttempts.firstAttempt = now;
    }

    userAttempts.count++;
    this.attempts.set(identifier, userAttempts);

    if (userAttempts.count > this.maxAttempts) {
      const timeLeft = Math.ceil(
        (this.windowMs - (now - userAttempts.firstAttempt)) / 1000 / 60,
      );
      return {
        allowed: false,
        message: `Too many login attempts. Please try again in ${timeLeft} minutes.`,
      };
    }

    return {
      allowed: true,
      attemptsLeft: this.maxAttempts - userAttempts.count,
    };
  }

  reset(identifier) {
    this.attempts.delete(identifier);
  }
}

export const loginRateLimiter = new RateLimiter(5, 15 * 60 * 1000);

/**
 * Secure token storage with encryption (basic obfuscation)
 * Note: This is NOT cryptographically secure, just adds a layer
 * For production, consider httpOnly cookies instead
 */
export const secureStorage = {
  setItem: (key, value) => {
    try {
      // Basic obfuscation (NOT real encryption)
      const obfuscated = btoa(JSON.stringify(value));
      localStorage.setItem(key, obfuscated);
    } catch (e) {
      console.error("Failed to store item securely");
    }
  },

  getItem: (key) => {
    try {
      const obfuscated = localStorage.getItem(key);
      if (!obfuscated) return null;
      return JSON.parse(atob(obfuscated));
    } catch (e) {
      console.error("Failed to retrieve item securely");
      return null;
    }
  },

  removeItem: (key) => {
    localStorage.removeItem(key);
  },
};

/**
 * Generate CSRF token (should be validated on backend)
 */
export const generateCSRFToken = () => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

/**
 * Check if running in production
 */
export const isProduction = () => {
  return process.env.NODE_ENV === "production";
};

/**
 * Safe console log that only works in development
 */
export const safeLog = (...args) => {
  if (!isProduction()) {
    console.log(...args);
  }
};
